import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { BalloonEventModel } from 'ts-core';

interface CreatorModalState {
  isOpen: boolean;
  geoLocation?: { lng: number; lat: number };
  editIdentifier?: string;
  editEvent?: BalloonEventModel;
  cloneEvent?: BalloonEventModel;
  isEditMode: boolean;
  openCreator: (geoLocation: { lng: number; lat: number }) => void;
  openCreatorNew: () => void;
  openCreatorForEdit: (identifier: string, event?: BalloonEventModel) => void;
  openCreatorForClone: (event: BalloonEventModel) => void;
  closeCreator: () => void;
  onSuccessCallback?: () => void;
  setOnSuccessCallback: (callback: () => void) => void;
}

const CreatorModalContext = createContext<CreatorModalState | null>(null);

export const CreatorModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [geoLocation, setGeoLocation] = useState<{ lng: number; lat: number } | undefined>();
  const [editIdentifier, setEditIdentifier] = useState<string | undefined>();
  const [editEvent, setEditEvent] = useState<BalloonEventModel | undefined>();
  const [cloneEvent, setCloneEvent] = useState<BalloonEventModel | undefined>();
  const [onSuccessCallback, setOnSuccessCallbackState] = useState<(() => void) | undefined>();

  const isEditMode = Boolean(editIdentifier);

  const openCreator = useCallback((location: { lng: number; lat: number }) => {
    setEditIdentifier(undefined);
    setEditEvent(undefined);
    setCloneEvent(undefined);
    setGeoLocation(location);
    setIsOpen(true);
  }, []);

  const openCreatorNew = useCallback(() => {
    setEditIdentifier(undefined);
    setEditEvent(undefined);
    setCloneEvent(undefined);
    setGeoLocation(undefined);
    setIsOpen(true);
  }, []);

  const openCreatorForEdit = useCallback((identifier: string, event?: BalloonEventModel) => {
    setGeoLocation(undefined);
    setCloneEvent(undefined);
    setEditIdentifier(identifier);
    setEditEvent(event);
    setIsOpen(true);
  }, []);

  const openCreatorForClone = useCallback((event: BalloonEventModel) => {
    setGeoLocation(undefined);
    setEditIdentifier(undefined);
    setEditEvent(undefined);
    setCloneEvent(event);
    setIsOpen(true);
  }, []);

  const closeCreator = useCallback(() => {
    setIsOpen(false);
    setGeoLocation(undefined);
    setEditIdentifier(undefined);
    setEditEvent(undefined);
    setCloneEvent(undefined);
  }, []);

  const setOnSuccessCallback = useCallback((callback: () => void) => {
    setOnSuccessCallbackState(() => callback);
  }, []);

  return (
    <CreatorModalContext.Provider
      value={{
        isOpen,
        geoLocation,
        editIdentifier,
        editEvent,
        cloneEvent,
        isEditMode,
        openCreator,
        openCreatorNew,
        openCreatorForEdit,
        openCreatorForClone,
        closeCreator,
        onSuccessCallback,
        setOnSuccessCallback,
      }}
    >
      {children}
    </CreatorModalContext.Provider>
  );
};

export function useCreatorModal(): CreatorModalState {
  const context = useContext(CreatorModalContext);
  if (!context) {
    throw new Error('useCreatorModal must be used within a CreatorModalProvider');
  }
  return context;
}

export function useCreatorModalSafe(): CreatorModalState | null {
  return useContext(CreatorModalContext);
}
